import FVector from '@data/geometry/FVector';
import InsidePolygonBlock from '@data/Simulations/pipeline/blocks/InsidePolygonBlock';
import { Pipeline, PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import { ControllerManager } from '@gui/controller/ControllerManager';
import useNotificationStore from '@gui/store/notification';
import { DesignSchemeRenderer } from '@rendering/DesignSchemes/DesignSchemeRenderer';
import type { Object3D, RendererBaseConfig, SchemeKey } from '@visualizer/types';
import { LayerKind, NotificationType, PipelinePhase } from '@visualizer/types';
import { SensorData } from 'Simulations/SensorPoint';
import { getGrid } from 'store/LoadingStore';
import Layer from '../../../Simulations/Layer';
import { DesignSchemeSet } from '../DesignSchemeSet';

/**
 * Base class for all design schemes.
 */
export abstract class DesignScheme implements Object3D {
    protected id: string;
    private disposed = false;

    // Hierarchy for State:
    protected readonly parentSet: DesignSchemeSet;

    // Layers & Footprint:
    protected linkedLayers: Map<LayerKind, Layer>;
    protected footprint: { positions: FVector[]; id: string };

    // Per-layer-kind pipelines & sensorDatas:
    protected pipelineByLayerKind: Map<LayerKind, Pipeline | null>;
    protected sensorsByLayerKind: Map<
        LayerKind,
        {
            data: SensorData[];
            minMax: [number, number];
        }
    >;
    // Tracks ids of sensors inside the polygon. Can't reuse sensorsByLayerKind as different layerKinds have different pipelines (not only insidePolygon):
    private lastSensorIdsByKind = new Map<LayerKind, number[]>();

    // Rendering:
    protected renderer: DesignSchemeRenderer;
    private readonly staleRenderers = new Set<DesignSchemeRenderer>();

    // Subscribed to GUI changes:
    private unsubscribeStoreWatcher?: () => void;

    constructor(
        footprint: FVector[],
        footprintId: string,
        set: DesignSchemeSet,
        initConfig: RendererBaseConfig,
        linkedLayers?: Map<LayerKind, Layer>
    ) {
        this.id = crypto.randomUUID();
        this.footprint = { positions: footprint, id: footprintId };

        // Hierarchy:
        this.parentSet = set;

        // Layers & Footprint Init:
        this.linkedLayers = linkedLayers ?? new Map<LayerKind, Layer>();
        if (!this.linkedLayers.has(LayerKind.MAIN)) this.linkedLayers.set(LayerKind.MAIN, getGrid().getActiveLayer());

        // Pipeline init:
        this.pipelineByLayerKind = new Map<LayerKind, Pipeline>();

        const basePipeline = new Pipeline();
        basePipeline.addBlock(new InsidePolygonBlock({ polygon: this.footprint.positions }));
        this.pipelineByLayerKind.set(LayerKind.MAIN, basePipeline);

        // Init renderer (needed before sensor init):
        this.renderer = this.createRenderer(initConfig);

        // Sensors init:
        this.sensorsByLayerKind = new Map<LayerKind, { data: SensorData[]; minMax: [number, number] }>();
        const mainLayer = this.linkedLayers.get(LayerKind.MAIN)!;
        const mainResult = this.runLayerPipelineForDesignScheme(mainLayer);
        this.updateSensors(mainResult, mainLayer.getID());

        // Initialization:
        this.init();
    }

    // =====================================
    // ABSTRACT PROPERTIES
    // =====================================

    // Abstract factories & config hooks:
    protected abstract createRenderer(config: RendererBaseConfig): DesignSchemeRenderer;
    abstract getRendererKey(): SchemeKey;

    // =====================================
    // LIFECYCLE
    // =====================================

    public init() {
        this.renderer.generateMeshWithLoading();
        this.notifyIfMeshCannotBeGenerated();
    }

    public draw() {
        this.renderer.setVisibility(true);
    }

    public hide() {
        this.renderer.setVisibility(false);
    }

    public update() {
        if (this.disposed || !this.renderer) return;

        // Update Renderer:
        const config = this.renderer.getConfig();
        const newRenderer = this.createRenderer(config);
        this.staleRenderers.add(this.renderer);
        this.renderer = newRenderer;

        // Callback to clean stale renderers (when new one completed):
        newRenderer.onGenerationComplete(() => {
            if (this.renderer !== newRenderer) {
                newRenderer.clean();
                return;
            }

            this.staleRenderers.forEach((renderer) => renderer.clean());
            this.staleRenderers.clear();

            // Correctly visualize mesh:
            const targetVisibility = config.active && this.parentSet.isVisible();
            newRenderer.setVisibility(targetVisibility);
            if (targetVisibility) this.draw();
            else this.hide();
        });

        // Correctly visualize Mesh:
        newRenderer.generateMeshWithLoading();
        this.notifyIfMeshCannotBeGenerated();
    }

    dispose() {
        if (this.disposed) return;
        this.disposed = true;

        // Unsubscribe from frame changes:
        this.unsubscribeStoreWatcher?.();
        this.unsubscribeStoreWatcher = undefined;

        // Dispose renderer and clear references:
        this.renderer?.clean();
        this.staleRenderers.forEach((renderer) => renderer.clean());
        this.staleRenderers.clear();
        (this as any).renderer = null;
        (this as any).sensorDatas = [];
        (this as any).footprint = [];
    }

    // =====================================
    // LAYER & FRAME MANAGEMENT
    // =====================================

    getLayer(kind: LayerKind): Layer | undefined {
        return this.linkedLayers.get(kind);
    }

    public updateLayer(newLayer: Layer, kind: LayerKind): void {
        // 1. Update runtime map:
        this.linkedLayers.set(kind, newLayer);

        // 2. Update state map:
        const dsController = ControllerManager.getInstance().getDesignSchemeController();
        dsController.updateDesignScheme(this.getDesignSchemeSet().getID(), this.getFootprintId(), this.getID(), {
            linkedLayerIds: this.getLinkedLayerIds(),
        });

        // 3. Re-run sensors for this updated layer:
        const result = this.runLayerPipelineForDesignScheme(newLayer);
        this.updateSensors(result, newLayer.getID());
    }

    public onFrameChanged(layerId: string) {
        const kinds = this.getKindsForLayerId(layerId);
        if (kinds.length === 0) return;

        // Both MAIN and FACADE share the same layer if both kinds exist:
        const layer = this.linkedLayers.get(kinds[0]);
        if (!layer) return;

        const result = this.runLayerPipelineForDesignScheme(layer);
        this.updateSensors(result, layerId);
    }

    public isLayerLinked(layerId: string): boolean {
        return this.getKindsForLayerId(layerId).length > 0;
    }

    protected getKindsForLayerId(layerId: string): LayerKind[] {
        const kinds: LayerKind[] = [];
        for (const [kind, layer] of this.linkedLayers.entries()) {
            if (layer.getID() === layerId) kinds.push(kind);
        }
        return kinds;
    }

    // =====================================
    // FOOTPRINT
    // =====================================

    updateFootprint(newFootprint: FVector[]) {
        this.footprint.positions = newFootprint;

        // Update pipeline:
        const mainPipeline = this.pipelineByLayerKind.get(LayerKind.MAIN)!;
        mainPipeline.clearPipeline();
        mainPipeline.addBlock(new InsidePolygonBlock({ polygon: this.footprint.positions }));

        // Run pipeline and update sensors:
        const mainLayer = this.linkedLayers.get(LayerKind.MAIN)!;
        const mainResult = this.runLayerPipelineForDesignScheme(mainLayer);
        this.updateSensors(mainResult, mainLayer.getID());

        // Redraw with new data:
        this.update();
    }

    // =====================================
    // PIPELINE & SENSORS
    // =====================================

    // Run regular pipeline for DesignSchemes (without DS post-blocks which are only visual effects):
    private runLayerPipelineForDesignScheme(layer: Layer): PipelineExecutionResult {
        return layer.runPipeline({
            suppressNotifications: true,
            capturePhase: PipelinePhase.Regular,
            octTree: getGrid().octTree!,
        });
    }

    runPipeline(layerSensors: SensorData[], minMax: [number, number]): PipelineExecutionResult {
        // Run DesignScheme base pipeline:
        const mainPipeline = this.pipelineByLayerKind.get(LayerKind.MAIN)!;
        mainPipeline.getBlocks().forEach((block) => block.clearCache()); // If update is called we need to update, no cache.
        const dsPipelineResults = mainPipeline.execute(layerSensors, minMax, { octTree: getGrid().octTree! });

        // Done:
        return dsPipelineResults;
    }

    public updatePipeline(kind: LayerKind, pipeline: Pipeline): void {
        this.pipelineByLayerKind.set(kind, pipeline);
    }

    getSensorDatasFor(kind: LayerKind): SensorData[] {
        return this.sensorsByLayerKind.get(kind)?.data ?? [];
    }

    getMinMaxFor(kind: LayerKind): [number, number] {
        return this.sensorsByLayerKind.get(kind)?.minMax ?? [0, 0];
    }

    public getUnfilteredSensorDatasFor(kind: LayerKind): SensorData[] {
        const layer = this.linkedLayers.get(kind);
        if (!layer) return [];
        return layer?.getActiveFrame().getSensors();
    }

    public updateSensors(pipelineResult: PipelineExecutionResult, layerId: string): void {
        const kinds = this.getKindsForLayerId(layerId);
        if (kinds.length === 0) return;

        // 1) Run MAIN pipeline ONCE (Get sensors inside Polygon):
        const baseResult = this.runPipeline(pipelineResult.data, pipelineResult.outputMinMax);

        // 2) Run per-layer pipelines:
        for (const kind of kinds) {
            let finalResult = baseResult;

            if (kind !== LayerKind.MAIN) {
                const extraPipeline = this.pipelineByLayerKind.get(kind);
                if (extraPipeline) {
                    extraPipeline.getBlocks().forEach((block) => block.clearCache()); // If update is called we need to update, no cache.
                    finalResult = extraPipeline.execute(baseResult.data, baseResult.outputMinMax, { octTree: getGrid().octTree! });
                }
            }

            this.lastSensorIdsByKind.set(
                kind,
                finalResult.data.map((d) => d.getID())
            );

            this.sensorsByLayerKind.set(kind, {
                data: finalResult.data,
                minMax: finalResult.outputMinMax,
            });
        }

        // 5) Update renderer ONCE
        this.update();
    }

    // =====================================
    // UTILITIES
    // =====================================

    getID() {
        return this.id;
    }

    setID(id: string) {
        this.id = id;
    }

    getFootprintId() {
        return this.footprint.id;
    }

    getFootprint() {
        return this.footprint.positions;
    }

    getRenderer() {
        return this.renderer;
    }

    isDisposed() {
        return this.disposed;
    }

    getDesignSchemeSet() {
        return this.parentSet;
    }

    protected getLinkedLayerIds(): Partial<Record<LayerKind, string>> {
        const ids: Partial<Record<LayerKind, string>> = {};

        this.linkedLayers?.forEach((layer, kind) => {
            ids[kind] = layer.getID();
        });

        return ids;
    }

    private notifyIfMeshCannotBeGenerated() {
        if (this.getFootprint().length === 0) return;

        const mainSensors = this.getSensorDatasFor(LayerKind.MAIN);
        const hasRenderableSensors = mainSensors.some((sensor) => sensor.getScalarValue() !== 0);

        if (hasRenderableSensors) return;

        useNotificationStore
            .getState()
            .showNotification(
                'The design scheme could not be generated because there are no active sensors (or no sensors with values greater than zero) in the selected area.',
                NotificationType.Warning
            );
    }
}
